// ===========================================
// Hybrid Booking Summary Cards Component
// ===========================================
// Summary statistics for hybrid booking analytics
// Phase 2 Priority 12: Reporting & Analytics - Week 4

import React from 'react';
import { Grid, Card, CardContent, Typography, Box, Skeleton } from '@mui/material';
import {
  LibraryMusic,
  CheckCircle,
  Schedule,
  EventAvailable,
  Cancel,
  CalendarToday,
} from '@mui/icons-material';
import { HybridBookingMetricsSummary } from '../../api/reports.api';
import { formatPercentage } from '../../hooks/useReports';

interface HybridBookingSummaryCardsProps {
  summary?: HybridBookingMetricsSummary;
  isLoading: boolean;
}

interface SummaryCardProps {
  title: string;
  value: string | number;
  icon: React.ReactNode;
  color?: string;
  subtitle?: string;
  isLoading: boolean;
}

const SummaryCard: React.FC<SummaryCardProps> = ({
  title,
  value,
  icon,
  color = 'primary.main',
  subtitle,
  isLoading,
}) => {
  if (isLoading) {
    return (
      <Card>
        <CardContent>
          <Skeleton variant="text" width="60%" />
          <Skeleton variant="text" width="40%" height={40} />
          {subtitle && <Skeleton variant="text" width="50%" />}
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Box
            sx={{
              bgcolor: color,
              borderRadius: '50%',
              p: 1,
              display: 'flex',
              mr: 2,
            }}
          >
            {icon}
          </Box>
          <Typography variant="subtitle2" color="text.secondary">
            {title}
          </Typography>
        </Box>
        <Typography variant="h4" component="div" sx={{ mb: 0.5 }}>
          {value}
        </Typography>
        {subtitle && (
          <Typography variant="caption" color="text.secondary">
            {subtitle}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
};

const HybridBookingSummaryCards: React.FC<HybridBookingSummaryCardsProps> = ({
  summary,
  isLoading,
}) => {
  const getCompletionColor = (rate: number): string => {
    if (rate >= 80) return '#5cb399'; // Green
    if (rate >= 60) return '#FFCE00'; // Yellow
    return '#FFAE9E'; // Coral/Red
  };

  const completionRate = summary?.overallCompletionRate ?? 0;
  const completionColor = getCompletionColor(completionRate);

  return (
    <Grid container spacing={3} sx={{ mb: 3 }}>
      <Grid item xs={12} sm={6} md={4}>
        <SummaryCard
          title="Total Hybrid Lessons"
          value={summary?.totalHybridLessons ?? 0}
          icon={<LibraryMusic sx={{ color: 'white' }} />}
          color="primary.main"
          isLoading={isLoading}
        />
      </Grid>
      <Grid item xs={12} sm={6} md={4}>
        <SummaryCard
          title="Overall Completion Rate"
          value={summary ? formatPercentage(completionRate) : '0%'}
          icon={<CheckCircle sx={{ color: 'white' }} />}
          color={completionColor}
          subtitle={
            completionRate >= 80
              ? 'Excellent booking completion'
              : completionRate >= 60
              ? 'Good booking completion'
              : 'Needs improvement'
          }
          isLoading={isLoading}
        />
      </Grid>
      <Grid item xs={12} sm={6} md={4}>
        <SummaryCard
          title="Average Booking Time"
          value={summary ? `${summary.averageBookingTime.toFixed(1)}h` : '0h'}
          icon={<Schedule sx={{ color: 'white' }} />}
          color="primary.main"
          subtitle="Time between booking and lesson"
          isLoading={isLoading}
        />
      </Grid>
      <Grid item xs={12} sm={6} md={4}>
        <SummaryCard
          title="Total Bookings Made"
          value={summary?.totalBookingsMade ?? 0}
          icon={<EventAvailable sx={{ color: 'white' }} />}
          color="primary.main"
          isLoading={isLoading}
        />
      </Grid>
      <Grid item xs={12} sm={6} md={4}>
        <SummaryCard
          title="Cancellation Rate"
          value={summary ? formatPercentage(summary.cancellationRate) : '0%'}
          icon={<Cancel sx={{ color: 'white' }} />}
          color={summary && summary.cancellationRate > 10 ? '#FFAE9E' : 'primary.main'}
          subtitle={
            summary && summary.cancellationRate > 10
              ? 'High cancellation rate'
              : 'Normal cancellation rate'
          }
          isLoading={isLoading}
        />
      </Grid>
      <Grid item xs={12} sm={6} md={4}>
        <SummaryCard
          title="Active Booking Weeks"
          value={summary?.activeBookingWeeks ?? 0}
          icon={<CalendarToday sx={{ color: 'white' }} />}
          color="primary.main"
          subtitle="Weeks with booking activity"
          isLoading={isLoading}
        />
      </Grid>
    </Grid>
  );
};

export default HybridBookingSummaryCards;
